import queryString from 'qs'
import { inline } from 'substyle'
import { useStyles } from '@signavio/ui'

import { ThemeT, utils } from '@signavio/effektif-commons'

import { getBaseUrl, getRootUrl } from '../../callApi'

import { useFileDownloadSecret, useOrganization } from '../../hooks'
import { userUtils } from '../../utils'
import { UserType } from '../../../types'

export type AvatarProps = {
  user: UserType

  bust?: number

  admin?: boolean
  circular?: boolean
  disabled?: boolean
  micro?: boolean
  small?: boolean
  large?: boolean
  medium?: boolean
}

function Avatar({
  user,
  bust,
  admin,
  disabled,
  micro,
  small,
  large,
  circular,
  medium,
}: AvatarProps) {
  const fileDownloadSecret = useFileDownloadSecret()
  const organization = useOrganization()

  const styles = useStyles(
    defaultStyles,
    {},
    {
      '&text': Boolean(user.color),
      '&disabled': Boolean(disabled),
      '&micro': Boolean(micro),
      '&small': Boolean(small),
      '&large': Boolean(large),
      '&medium': Boolean(medium),
      '&regular': Boolean(!micro && !large && !small),
      '&circular': Boolean(circular),
    }
  )

  if (user.color) {
    return (
      <div {...styles}>
        <div
          data-testid="user-avatar"
          {...inline(styles('text'), { backgroundColor: user.color })}
          title={userUtils.name(user)}
        >
          {userUtils.initials(user) || '?'}
        </div>
      </div>
    )
  }

  const src = `${
    admin ? `${getRootUrl()}/admin` : getBaseUrl(organization)
  }/users/${user.id}/picture`

  const query = queryString.stringify({
    token: fileDownloadSecret,
    _dc: bust,
  })

  return (
    <div {...styles}>
      <img
        {...styles('image')}
        src={`${src}${query ? `?${query}` : ''}`}
        alt={userUtils.name(user)}
        title={userUtils.name(user)}
      />
    </div>
  )
}

const config = (size: number, fontSize: string) => ({
  width: size,
  height: size,

  image: {
    display: 'block',

    overflow: 'hidden',

    width: size,
    height: size,
  },

  text: {
    display: 'flex',

    justifyContent: 'center',
    alignItems: 'center',

    color: 'white',

    fontSize,

    userSelect: 'none',

    overflow: 'hidden',

    width: size,
    height: size,
  },
})

export const size = {
  micro: 20,
  small: 26,
  normal: 40,
  large: 80,
}

const defaultStyles = ({ font }: ThemeT) => {
  return {
    position: 'relative',
    display: 'flex',

    justifyContent: 'center',
    alignItems: 'center',

    zIndex: 0,

    ...config(size.normal, font.size.normal),

    '&mono': {
      ...utils.grayscale(),
    },

    '&micro': config(size.micro, font.size.small),
    '&small': config(size.small, font.size.small),
    '&large': config(size.large, font.size.normal),

    '&disabled': {
      filter: 'grayscale(100%)',
      opacity: 0.7,
    },

    '&circular': {
      image: {
        borderRadius: '50%',
      },

      text: {
        borderRadius: '50%',
      },

      '&regular': {
        image: {
          height: 32,
          width: 32,
        },

        text: {
          height: 32,
          width: 32,
        },
      },

      '&medium': {
        image: {
          height: 40,
          width: 40,
        },

        text: {
          height: 40,
          width: 40,
        },
      },
    },
  }
}

export default Avatar
